import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { researchApi } from "core/api";
import {
  ImportLinkItem,
  ImportLinksResult,
  ImportParseResult,
} from "core/types/search.types";
import {
  IMPORT_ACCEPT,
  IMPORT_BATCH_SIZE,
  IMPORT_MAX_BYTES,
  chunkItems,
  importErrorKey,
  newImportItems,
} from "./import-links.utils";

type Props = {
  open: boolean;
  searchId: string;
  onClose: () => void;
  // Almeno un link aggiunto: la pagina ricarica la ricerca (come dopo l'aggiunta manuale).
  // Si chiama alla CHIUSURA del riepilogo, non subito: il ricaricamento smonta il
  // dettaglio della ricerca (e con lui questo dialog), e il riepilogo andrebbe perso.
  onImported: () => void;
};

type Step = "select" | "preview" | "importing" | "done";

type Summary = {
  added: number;
  withoutPreview: number;
  duplicates: number;
  failed: { url: string; reason: string }[];
};

const emptySummary: Summary = {
  added: 0,
  withoutPreview: 0,
  duplicates: 0,
  failed: [],
};

const statusChip = (
  item: ImportLinkItem,
  t: (key: string) => string
): React.ReactElement => {
  if (item.duplicate === "in_search") {
    return (
      <Chip size="small" label={t("importLinks.status.in_search")} />
    );
  }
  if (item.duplicate === "in_file") {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={t("importLinks.status.in_file")}
      />
    );
  }
  return (
    <Chip size="small" color="success" label={t("importLinks.status.new")} />
  );
};

/**
 * Import di molti link in una ricerca da un file Excel/CSV/TXT (richiesta AGE).
 * 1) il backend legge il file e restituisce l'anteprima (nessuna scrittura);
 * 2) i link nuovi partono a lotti da 25, in sequenza, con la barra di avanzamento;
 * 3) riepilogo: aggiunti, gia' presenti, non riusciti.
 */
export const ImportLinksDialog: React.FC<Props> = ({
  open,
  searchId,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("select");
  const [fileName, setFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<ImportParseResult | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState<Summary>(emptySummary);

  const toImport = preview ? newImportItems(preview.items) : [];
  const counts = {
    new: toImport.length,
    inSearch:
      preview?.items.filter((i) => i.duplicate === "in_search").length ?? 0,
    inFile: preview?.items.filter((i) => i.duplicate === "in_file").length ?? 0,
  };

  const reset = () => {
    setStep("select");
    setFileName("");
    setIsParsing(false);
    setError("");
    setPreview(null);
    setProgress({ done: 0, total: 0 });
    setSummary(emptySummary);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    // Durante l'invio non si chiude: i lotti successivi partirebbero comunque e il
    // riepilogo andrebbe perso.
    if (step === "importing") return;
    const refresh = step === "done" && summary.added > 0;
    reset();
    onClose();
    if (refresh) onImported();
  };

  const onFileChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setError("");
    setPreview(null);
    setFileName(file.name);
    if (file.size > IMPORT_MAX_BYTES) {
      setError(t("importLinks.errors.too_large"));
      return;
    }
    setIsParsing(true);
    try {
      const result = await researchApi.parseImportLinks(searchId, file);
      setPreview(result);
      setStep("preview");
    } catch (err) {
      setError(t(importErrorKey(err)));
    } finally {
      setIsParsing(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onImport = async () => {
    if (!preview || toImport.length === 0) return;
    const batchSize = Math.min(
      IMPORT_BATCH_SIZE,
      preview.maxItemsPerCall || IMPORT_BATCH_SIZE
    );
    const batches = chunkItems(toImport, batchSize);
    const acc: Summary = { ...emptySummary, failed: [] };
    setStep("importing");
    setProgress({ done: 0, total: toImport.length });
    let done = 0;
    for (const batch of batches) {
      try {
        const res: ImportLinksResult = await researchApi.importLinks(
          searchId,
          batch.map((item) => ({ url: item.url, note: item.note || undefined }))
        );
        acc.added += res.added.length;
        acc.withoutPreview += res.added.filter((a) => !a.previewOk).length;
        acc.duplicates += res.duplicates.length;
        acc.failed.push(...res.failed);
      } catch (err) {
        // Lotto non riuscito (rete, 5xx): i suoi link finiscono tra i non riusciti e si
        // prosegue con gli altri, che sono indipendenti.
        acc.failed.push(
          ...batch.map((item) => ({ url: item.url, reason: "request_error" }))
        );
      }
      done += batch.length;
      setProgress({ done, total: toImport.length });
    }
    // gia' presenti = scartati in anteprima + quelli che il backend ha trovato doppi
    acc.duplicates += counts.inSearch + counts.inFile;
    setSummary(acc);
    setStep("done");
  };

  const reasonLabel = (reason: string) =>
    t(`importLinks.reasons.${reason}`, {
      defaultValue: t("importLinks.reasons.generic"),
    });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown={step === "importing"}
    >
      <DialogTitle>{t("importLinks.title")}</DialogTitle>
      <DialogContent>
        {step === "select" && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} mb={0.5}>
                {t("importLinks.format_title")}
              </Typography>
              <Typography variant="body2">
                {t("importLinks.format_rows")}
              </Typography>
              <Typography variant="body2">
                {t("importLinks.format_note")}
              </Typography>
              <Typography variant="body2">
                {t("importLinks.format_limits")}
              </Typography>
              <Typography variant="body2">
                {t("importLinks.format_cleanup")}
              </Typography>
            </Alert>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileRoundedIcon />}
                disabled={isParsing}
              >
                {t("importLinks.choose_file")}
                <input
                  ref={inputRef}
                  hidden
                  type="file"
                  accept={IMPORT_ACCEPT}
                  onChange={onFileChange}
                />
              </Button>
              {isParsing && <CircularProgress size={24} color="secondary" />}
              {fileName && (
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
              )}
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {step === "preview" && preview && (
          <>
            <Typography variant="body2" mb={1}>
              {t("importLinks.preview_summary", {
                file: fileName,
                rows: preview.totalRows,
                links: preview.items.length,
              })}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip
                color="success"
                label={t("importLinks.count_new", { count: counts.new })}
              />
              <Chip
                label={t("importLinks.count_in_search", {
                  count: counts.inSearch,
                })}
              />
              <Chip
                color="warning"
                variant="outlined"
                label={t("importLinks.count_in_file", { count: counts.inFile })}
              />
            </Box>
            {preview.items.length === 0 ? (
              <Alert severity="warning">{t("importLinks.no_links")}</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("importLinks.col_row")}</TableCell>
                      <TableCell>{t("importLinks.col_link")}</TableCell>
                      <TableCell>{t("importLinks.col_note")}</TableCell>
                      <TableCell>{t("importLinks.col_status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.items.map((item, index) => (
                      <TableRow key={`${item.row}-${index}`}>
                        <TableCell>{item.row}</TableCell>
                        <TableCell
                          sx={{ maxWidth: 360, wordBreak: "break-all" }}
                        >
                          <Link
                            href={item.normalizedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.normalizedUrl}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>{item.note}</TableCell>
                        <TableCell>{statusChip(item, t)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {preview.skipped.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" fontWeight={700}>
                  {t("importLinks.skipped_title", {
                    count: preview.skipped.length,
                  })}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3, maxHeight: 140, overflow: "auto" }}>
                  {preview.skipped.map((s, index) => (
                    <li key={`${s.row}-${index}`}>
                      <Typography variant="body2" color="text.secondary">
                        {t("importLinks.skipped_row", {
                          row: s.row,
                          reason: reasonLabel(s.reason),
                        })}
                        {s.text ? ` — ${s.text}` : ""}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {step === "importing" && (
          <Box py={2}>
            <Typography variant="body2" mb={1}>
              {t("importLinks.importing", {
                done: progress.done,
                total: progress.total,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                progress.total ? (progress.done / progress.total) * 100 : 0
              }
            />
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              {t("importLinks.importing_hint")}
            </Typography>
          </Box>
        )}

        {step === "done" && (
          <>
            <Alert
              severity={summary.failed.length ? "warning" : "success"}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                {t("importLinks.result_added", { count: summary.added })}
              </Typography>
              {summary.withoutPreview > 0 && (
                <Typography variant="body2">
                  {t("importLinks.result_without_preview", {
                    count: summary.withoutPreview,
                  })}
                </Typography>
              )}
              <Typography variant="body2">
                {t("importLinks.result_duplicates", {
                  count: summary.duplicates,
                })}
              </Typography>
              <Typography variant="body2">
                {t("importLinks.result_failed", {
                  count: summary.failed.length,
                })}
              </Typography>
            </Alert>
            {summary.failed.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 3, maxHeight: 160, overflow: "auto" }}>
                {summary.failed.map((f, index) => (
                  <li key={`${f.url}-${index}`}>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {f.url} — {reasonLabel(f.reason)}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === "preview" && (
          <>
            <Button onClick={reset}>{t("importLinks.other_file")}</Button>
            <Button onClick={handleClose}>{t("importLinks.cancel")}</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={toImport.length === 0}
            >
              {t("importLinks.import_n", { count: toImport.length })}
            </Button>
          </>
        )}
        {step === "select" && (
          <Button onClick={handleClose}>{t("importLinks.cancel")}</Button>
        )}
        {step === "done" && (
          <Button variant="contained" onClick={handleClose}>
            {t("importLinks.close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportLinksDialog;
